import type {
  BatchSelectionContextValue,
  CheckRegisterReport,
  CheckRunExpandableRowState,
  DropoffTimelineEvent,
  Group,
  PaymentFileExpandableRowState,
  PaymentMethodConfig,
  StatusConfig,
  StatusGroup,
  TimelineEvent,
  TimelineEventConfig
} from './types'

import {
  CloudUploadOutlined,
  FileExcelFilled,
  FilePdfFilled,
  FileTextFilled,
  FileZipFilled,
  FireFilled,
  HistoryOutlined,
  MailOutlined,
  RollbackOutlined,
  WarningOutlined
} from '@ant-design/icons'
import { createContext } from 'react'

import { PickupSource } from './types'

export const groupMetaKeyNameMapper: Record<keyof Group, string | undefined> = {
  id: 'ID',
  number: 'Number',
  name: 'Name',
  release_type: 'Release Type',
  release_day: 'Release Day',
  zelis_eligible: 'Zelis Eligible',
  activation_date: 'Activation Date',
  checks_only: 'Checks Only'
}

export const reportFileTypeMapper: Record<CheckRegisterReport['type'], string> = {
  detail: '35',
  summary: '34',
  'one-page': '36',
  winsupply: '37',
  'network-summary': '38',
  'network-detail': '39',
  'allied-oms': '40'
}

export const releaseTypeMapper: Record<string, string> = {
  email: 'Email',
  deposit: 'Deposit',
  auto: 'Auto',
  'level-funded': 'Level Funded'
}

export const weekDays = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday'
]

export const paymentMethods: PaymentMethodConfig[] = [
  {
    label: 'Checks',
    value: 'Check'
  },
  {
    label: 'Epay',
    value: 'Epay'
  },
  {
    label: 'ACH',
    value: 'ACH'
  },
  {
    label: 'Network',
    value: 'Network'
  }
]

export const reportsTypes: PaymentMethodConfig[] = [
  {
    label: 'Checks',
    value: 'Check'
  },
  {
    label: 'Epay',
    value: 'Epay'
  },
  {
    label: 'ACH',
    value: 'ACH'
  },
  {
    label: 'Network Detail',
    value: 'Network Detail'
  },
  {
    label: 'Network Summary',
    value: 'Network Summary'
  }
]

export const baseCellClassName = 'text-xs font-lato'
export const baseHeaderCellClassName = 'text-[0.675rem] uppercase font-lato !bg-transparent'

export const statusMapper: Record<string, StatusConfig> = {
  '1': {
    name: 'Loaded',
    color: 'blue'
  },
  '2': {
    name: 'Waiting: ePay',
    color: 'blue'
  },
  '3': {
    name: 'Waiting: Status',
    color: 'orange'
  },
  '4': {
    name: 'Waiting: PDF',
    color: 'blue'
  },
  '5': {
    name: 'CR Report Compiled',
    color: 'green'
  },
  '6': {
    name: 'Error: Load',
    color: 'red'
  },
  '7': {
    name: 'Error: Check Total',
    color: 'red'
  },
  '8': {
    name: 'Voided',
    color: 'cyan'
  },
  '9': {
    name: 'Data Transferred',
    color: 'blue'
  },
  '10': {
    name: 'Complete',
    color: 'blue'
  },
  '11': {
    name: 'Ready to Build',
    color: 'blue'
  },
  '12': {
    name: 'Waiting: Daily Reports',
    color: 'blue'
  },
  '13': {
    name: 'Building: Manual',
    color: 'blue'
  },
  '14': {
    name: 'Error: Build',
    color: 'red'
  },
  '15': {
    name: 'Waiting: Voids',
    color: 'blue'
  },
  '16': {
    name: 'Totals Mismatch',
    color: 'red'
  },
  mixed: {
    name: 'Mixed',
    color: 'orange'
  },
  'no-payment-file': {
    name: 'No Payment File',
    color: 'orange'
  },
  deleted: {
    name: 'Deleted',
    color: 'orange'
  }
}

export const statusGroups: StatusGroup[] = [
  {
    name: 'Success',
    value: 'success',
    className: 'text-green-700',
    statusIds: ['5', '8']
  },
  {
    name: 'Error',
    value: 'error',
    className: 'text-red-700',
    statusIds: ['6', '7', '14', '16']
  },
  {
    name: 'Pending',
    value: 'pending',
    className: 'text-blue-700',
    statusIds: ['1', '2', '4', '9', '11', '12', '13', '15']
  }
]

export const checkRunExpandableRowInitialState: CheckRunExpandableRowState = {
  expandedRowIds: []
}

export const pickupSourceNameMapper: Record<PickupSource, string> = {
  [PickupSource.S3]: 'S3',
  [PickupSource.SFTP]: 'SFTP'
}

export const reportTypeNameMapper: Record<string, string> = {
  detail: 'Lucent Detail',
  summary: 'Lucent Summary',
  'network-detail': 'Network Detail',
  'network-summary': 'Network Summary',
  'one-page': 'One Page',
  winsupply: 'Winsupply',
  'allied-oms': 'Allied OMS'
}

export const eventFileTypeConfigMapper: Record<string, TimelineEventConfig> = {
  'cr-report': {
    title(event: TimelineEvent): string {
      if (event.type !== 'cr-report' || !event.data || typeof event.data.type !== 'string') {
        return 'CR Report'
      }

      return `CR Report: ${reportTypeNameMapper[event.data.type] ?? event.data.type}`
    },
    color: 'text-lucent-green',
    icon: FileExcelFilled
  },

  'lcr-file': {
    title: 'LCR File',
    color: 'text-lucent-red',
    icon: FilePdfFilled
  },

  'lcr-file-manual': {
    title: 'Uploaded LCR File',
    color: 'text-lucent-red',
    icon: FilePdfFilled
  },

  'scheduled-file': {
    title: 'Scheduled File',
    color: 'text-lucent-primary',
    icon: FileTextFilled
  },

  'sent-to-zelis': {
    title: 'Zelis Prepass',
    color: 'text-lucent-primary',
    icon: FileTextFilled
  },

  'received-from-zelis': {
    title: 'Zelis Epay',
    color: 'text-lucent-primary',
    icon: FileTextFilled
  },

  'payment-file': {
    title: 'Payment File',
    color: 'text-lucent-text',
    icon: FileZipFilled
  },

  'payment-file-manual': {
    title: 'Uploaded Payment File',
    color: 'text-lucent-text',
    icon: FileZipFilled
  },

  notification: {
    title: 'Notification',
    color: 'text-lucent-text',
    icon: MailOutlined
  },

  rerun: {
    title: 'Rerun',
    color: 'text-lucent-text',
    icon: HistoryOutlined
  },

  void: {
    title: 'Void',
    color: 'text-orange-600',
    icon: FireFilled
  },

  restore: {
    title: 'Restore',
    color: 'text-green-600',
    icon: RollbackOutlined
  },

  nomi835: {
    title: 'NOMI 835',
    color: 'text-lucent-primary',
    icon: FileTextFilled
  },

  nomi_status_file: {
    title: 'NOMI Status Report',
    color: 'text-lucent-primary',
    icon: FileTextFilled
  },

  dropoff: {
    title(event: TimelineEvent): string {
      const e = event as DropoffTimelineEvent
      switch (e.data.status) {
        case 'failed': {
          return 'Drop off failed'
        }
        case 'scheduled': {
          return 'Drop off scheduled'
        }
        default: {
          return 'Drop off'
        }
      }
    },
    color: 'text-lucent-text',
    icon: CloudUploadOutlined
  },

  'single-payment-type-warning': {
    title: 'Single Payment Type Warning',
    color: 'text-orange-500',
    icon: WarningOutlined
  },

  'single-provider-warning': {
    title: 'Single Provider Warning',
    color: 'text-orange-500',
    icon: WarningOutlined
  }
}

export const timelineEventTypes: Array<TimelineEvent['type']> = [
  'cr-report',
  'lcr-file',
  'lcr-file-manual',
  'scheduled-file',
  'sent-to-zelis',
  'received-from-zelis',
  'payment-file',
  'payment-file-manual',
  'notification',
  'rerun',
  'void',
  'restore',
  'nomi835',
  'nomi_status_file',
  'single-payment-type-warning',
  'single-provider-warning'
]

export const paymentFileExpandableRowInitialState: PaymentFileExpandableRowState = {
  onHoldModal: {
    form: {
      reason: ''
    },

    isOpen: false,
    eobIds: [],
    isSubmitting: false
  },

  expandedRowIds: []
}

export const rerunActionKeyMapper: Record<string, string> = {
  'reset-claim-file-status-id': 'Reset claim file status ID',
  'reset-totals-checked': 'Reset totals checked',
  'sum-job': 'Sum job',
  'totals-job': 'Totals job',
  'check-run-workflow': 'Check run workflow'
}

export const BatchSelectionContext = createContext<BatchSelectionContextValue>({
  selectedCheckRunIds: [],
  selectedPaymentFileIds: [],
  selectedEOBIds: [],
  selectedClaimIds: [],

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onSelectCheckRuns: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onSelectPaymentFiles: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onSelectEOBs: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onSelectClaims: () => {}
})

export const AVAILABLE_FILE_TYPES_MAPPER: Record<string, string> = {
  'lcr-file': 'application/pdf',
  'lcr-file-manual': 'application/pdf',
  'scheduled-file': 'text/plain',
  'sent-to-zelis': 'text/plain',
  'received-from-zelis': 'text/plain',
  nomi835: 'text/plain',
  nomi_status_file: 'text/csv',
  'payment-file': 'text/plain',
  'payment-file-manual': 'text/plain'
}
